import './escolha-modulo.css';
import Links from '@/shared/constants/navegacao';
import MyContext from '@/shared/contexts/AppContext';
import { CarregarPermissoes, ValidarPermissao, getLoginTokenFromStorage } from '@/modules/system/auth/application/auth.service';
import { Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { LuCalendar, LuCheck, LuLogOut, LuScale, LuScrollText, LuUsers } from 'react-icons/lu';
import { ModuloSistema } from '@/shared/constants/modulos-sistema.enum';
import { PermissionEnum } from '@/modules/system/papeis/enum/permission.enum';
import { RoleEnum } from '@/modules/system/papeis/enum/role.enum';
import { ToastContainer } from 'react-toastify';
import { getModuloRelatoriosAtividadesHabilitado } from '@/modules/system/configuracoes/modulo-relatorios-atividades-habilitado';
import { useNavigate } from 'react-router-dom';

function IconLotacoes() {
  return <LuScale size={44} strokeWidth={1.35} aria-hidden />;
}

function IconMovimentacoes() {
  return <LuUsers size={44} strokeWidth={1.35} aria-hidden />;
}

function IconFolhaFrequencia() {
  return (
    <span className="escolha-modulo-icon-stack" aria-hidden>
      <LuCalendar size={38} strokeWidth={1.35} />
      <span className="escolha-modulo-icon-stack-overlay">
        <LuCheck size={14} strokeWidth={2.5} />
      </span>
    </span>
  );
}

function IconRelatorios() {
  return <LuScrollText size={42} strokeWidth={1.35} aria-hidden />;
}

export default function EscolhaModulo() {
  const navigate = useNavigate();
  const context = useContext(MyContext);
  const TOKEN = getLoginTokenFromStorage();
  const [isNotLoggedIn] = useState(TOKEN === null || TOKEN === undefined);
  const autoSelectedRef = useRef(false);

  if (!context) throw new Error('DisplayComponent deve ser usado dentro de um MyProvider');
  const { state, setState } = context;

  const permissoes = useMemo(() => CarregarPermissoes(), []);

  const permissaoFerias = ValidarPermissao(permissoes, [{ role: RoleEnum.LOTACAO, level: PermissionEnum.READ }]);
  const permissaoMovimentacoes = ValidarPermissao(permissoes, [{ role: RoleEnum.MOVIMENTACAO, level: PermissionEnum.READ }]);
  const permissaoFolhas = ValidarPermissao(permissoes, [{ role: RoleEnum.FOLHA, level: PermissionEnum.READ }]);
  const permissaoDefensor = ValidarPermissao(permissoes, [{ role: RoleEnum.DEFENSOR, level: PermissionEnum.READ }]);
  const permissaoColaborador = ValidarPermissao(permissoes, [{ role: RoleEnum.COLABORADOR, level: PermissionEnum.READ }]);
  const permissaoEstagiario = ValidarPermissao(permissoes, [{ role: RoleEnum.ESTAGIARIO, level: PermissionEnum.READ }]);

  const [moduloRelatoriosAtividadesHabilitado, setModuloRelatoriosAtividadesHabilitado] = useState<boolean | null>(null);

  useEffect(() => {
    void getModuloRelatoriosAtividadesHabilitado().then(setModuloRelatoriosAtividadesHabilitado);
  }, []);

  const isViaToken = !state.username && state.cpf;
  const permissaoFolhasViaToken = isViaToken || permissaoFolhas;
  const permissaoRelatoriosViaToken = isViaToken;

  const ehApenasEstagiario = permissaoEstagiario && !permissaoDefensor && !permissaoColaborador;
  const podeVerRelatoriosAtividades =
    (permissaoMovimentacoes || permissaoRelatoriosViaToken) &&
    !ehApenasEstagiario &&
    moduloRelatoriosAtividadesHabilitado !== false;

  const handleChageModulo = (novoModulo: ModuloSistema) => {
    const navbar = Boolean(state.username?.trim());
    setState({ ...state, modulo: novoModulo, navbar });

    if (novoModulo === ModuloSistema.RelatoriosAtividades) {
      navigate(`${Links.Relatorios}`);
    } else {
      navigate(`${Links.Index}`);
    }
  };

  useEffect(() => {
    if (isNotLoggedIn) navigate(`${Links.Auth_Login}`);
  }, []);

  const cards = useMemo(
    () =>
      [
        {
          modulo: ModuloSistema.Ferias as const,
          visible: permissaoFerias,
          title: 'Vínculos',
          description:
            'Gestão de lotações, vínculos e eventos de pessoal: abonos, faltas, férias e demais ações vinculadas à lotação.',
          icon: <IconLotacoes />,
        },
        {
          modulo: ModuloSistema.Movimentacoes as const,
          visible: permissaoMovimentacoes,
          title: 'Movimentações',
          description:
            'Registro de atendimentos e movimentações processuais relacionados à atuação da defensoria pública.',
          icon: <IconMovimentacoes />,
        },
        {
          modulo: ModuloSistema.FolhaFrequencia as const,
          visible: permissaoFolhasViaToken,
          title: 'Folhas de Frequências - Estagiários',
          description:
            'Emissão e acompanhamento das folhas de frequência de estagiários, com controle de períodos e assinaturas.',
          icon: <IconFolhaFrequencia />,
        },
        {
          modulo: ModuloSistema.RelatoriosAtividades as const,
          visible: podeVerRelatoriosAtividades,
          title: 'Relatórios de Atividades - Defensores',
          description:
            'Elaboração e trâmite dos relatórios de atividades dos defensores (cível e criminal), inclusive envio à Corregedoria.',
          icon: <IconRelatorios />,
        },
      ].filter((c) => c.visible),
    [
      permissaoFerias,
      permissaoMovimentacoes,
      permissaoFolhasViaToken,
      podeVerRelatoriosAtividades,
    ],
  );

  useEffect(() => {
    if (isNotLoggedIn) return;
    if (autoSelectedRef.current) return;
    if (moduloRelatoriosAtividadesHabilitado === null) return;
    if (cards.length !== 1) return;

    autoSelectedRef.current = true;
    handleChageModulo(cards[0].modulo);
  }, [cards, handleChageModulo, isNotLoggedIn, moduloRelatoriosAtividadesHabilitado]);

  return (
    <Fragment>
      <ToastContainer position="top-center" />
      <section className="ftco-section">
        <div className="loginrow w-100 justify-content-center">
          <div className="escolha-modulo-wrap">
            <header className="escolha-modulo-heading">
              <div className="escolha-modulo-heading-row">
                <h2 className="mb-0">Escolha o Módulo</h2>
                <a
                  href={Links.Auth_Logout}
                  className="escolha-modulo-logout"
                  title="Sair"
                >
                  <LuLogOut size={18} strokeWidth={1.9} aria-hidden />
                  Sair
                </a>
              </div>
              <p id="escolha-modulo" className="mb-0">
                Selecione abaixo a área do SGI que deseja acessar nesta sessão.
              </p>
            </header>

            <ul className="escolha-modulo-grid">
              {cards.map((c) => (
                <li key={c.modulo}>
                  <button
                    type="button"
                    className="escolha-modulo-card"
                    onClick={() => handleChageModulo(c.modulo)}
                    aria-describedby="escolha-modulo"
                  >
                    <span className="escolha-modulo-card-icon">{c.icon}</span>
                    <span className="escolha-modulo-card-body">
                      <span className="escolha-modulo-card-title">{c.title}</span>
                      <p className="escolha-modulo-card-desc">{c.description}</p>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </Fragment>
  );
}
